import { CustomRequestHeaderUnion } from '@blk/aw-bff-typescript-models';
import { AxiosRequestConfig, AxiosResponseHeaders, AxiosResponseTransformer } from 'axios';
import { PostCloakDataParams, PostCloakDataSearchParams, SENSITIVE_DATA_BRANDED_TYPE } from '../api/SensitiveData';
import { HeaderRequestId, setHeaderRequestId } from './HeaderUtils';

/**
 * Generates the Axios request configuration object.
 * @param params - The parameters for the request.
 * @param customHeaders - The custom request headers.
 * @returns The Axios request configuration object.
 */
export const generateRequestConfig = (
  params: PostCloakDataParams | PostCloakDataSearchParams,
  customHeaders: CustomRequestHeaderUnion[]
): AxiosRequestConfig => {
  const config: AxiosRequestConfig = { params };
  setHeaderRequestId(config);
  const existingHeaders = config.headers;
  if (customHeaders.length) {
    const shouldRemoveBlkDefaultRequestId = customHeaders.some((header) => header.type === 'blkRequestId');
    const additionalHeaders = customHeaders.reduce<{ [key: string]: string }>((total, header) => {
      switch (header.type) {
        case 'blkRequestId': {
          if (header.header === '') {
            // If header is configured as empty string, do not append to header
            break;
          }
          total[header.header] = existingHeaders?.[HeaderRequestId.NAME];
          break;
        }
        case 'enforceWithCredentials':
          config.withCredentials = true;
          break;
        case 'static': {
          total[header.header] = header.value;
          if (header.header === 'Authorization') {
            config.withCredentials = true;
          }
          break;
        }
        default: {
          // Do nothing
          break;
        }
      }
      return total;
    }, {});
    config.headers = { ...config.headers, ...additionalHeaders };
    shouldRemoveBlkDefaultRequestId && delete config.headers[HeaderRequestId.NAME];
  }
  return config;
};

/**
 * Adds the branded type string to the metadata object.
 * @param metadata - The metadata object.
 * @returns The updated metadata object.
 */
export const addSensitiveDataBrandedType = (metadata: { [key: string]: string }): { [key: string]: string } =>
  Object.fromEntries(Object.entries(metadata).map(([key, value]) => [key, `${SENSITIVE_DATA_BRANDED_TYPE}${value}`]));

/**
 * Sanitizes the branded type string from the metadata object.
 * @param metadata - The metadata object.
 * @returns The sanitized metadata object.
 */
export const sanitizeSensitiveDataBrandedType = (metadata: { [key: string]: string }): { [key: string]: string } =>
  Object.fromEntries(
    Object.entries(metadata).map(([key, value]) => [key, value.replace(SENSITIVE_DATA_BRANDED_TYPE, '')])
  );

// do not use this transformer now as it needs to be improved.
/**
 * Transforms the cloak response data.
 * @param data - The cloak response data.
 * @param headers
 * @param status
 * @returns The transformed response data.
 */
export const transformCloakResponse: AxiosResponseTransformer = (
  data: unknown,
  headers: AxiosResponseHeaders,
  status?: number
) => {
  // do not transform the response if data type is not string or content type is not application/json
  if (!data || typeof data !== 'string' || !headers.getContentType()?.toString().includes('application/json')) {
    return data;
  }
  const parsedData = JSON.parse(data);
  // do not transform the response if status code is not 200
  if (status !== 200) {
    return parsedData;
  }
  return Array.isArray(parsedData)
    ? parsedData.map((datum) => ({
        ...datum,
        metadata: addSensitiveDataBrandedType(datum.metadata),
      }))
    : { ...parsedData, metadata: addSensitiveDataBrandedType(parsedData.metadata) };
};
